(() => {
  window.WTCTheme?.register('[data-about-story]', (root) => {
    const controller = new AbortController();
    const { signal } = controller;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = matchMedia('(min-width: 961px)');
    const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
    const media = root.querySelector('[data-story-media]');
    const photos = [...root.querySelectorAll('[data-story-photo]')];
    const captions = [...root.querySelectorAll('[data-story-caption]')];
    const entries = [...root.querySelectorAll('[data-story-chapter]')].map((detail) => ({
      detail,
      trigger: detail.querySelector('summary'),
      panel: detail.querySelector('.wtc-story-panel'),
      target: detail.open,
      animation: null,
      generation: 0,
    }));
    const ease = 'cubic-bezier(.22, 1, .36, 1)';
    const merchantMotion = root.dataset.motionEnabled !== 'false';
    let active = 0;
    let visible = false;
    let entered = false;
    let scrollFrame = 0;
    let pointerFrame = 0;
    let interaction = 0;
    let entranceAnimations = [];
    const motionAllowed = () => merchantMotion && !reduced.matches && !document.hidden;
    const resetPointer = () => {
      cancelAnimationFrame(pointerFrame);
      pointerFrame = 0;
      media.style.removeProperty('--story-mx');
      media.style.removeProperty('--story-my');
    };
    const stopEntrance = () => {
      entranceAnimations.forEach((animation) => animation.cancel());
      entranceAnimations = [];
    };
    const settle = (entry) => {
      entry.generation += 1;
      entry.animation?.cancel();
      entry.animation = null;
      entry.detail.open = entry.target;
      entry.trigger.setAttribute('aria-expanded', String(entry.target));
      entry.panel.inert = !entry.target;
    };
    const select = (index) => {
      active = index;
      root.dataset.story = String(index);
      entries.forEach((entry, i) => entry.detail.classList.toggle('is-active', i === index));
      photos.forEach((photo, i) => {
        photo.classList.toggle('is-active', i === index);
        photo.setAttribute('aria-hidden', String(i !== index));
      });
      captions.forEach((caption, i) => { caption.hidden = i !== index; });
    };
    const setOpen = (entry, open) => {
      if (entry.target === open) return;
      const start = entry.detail.open ? entry.panel.getBoundingClientRect().height : 0;
      const opacity = entry.detail.open ? getComputedStyle(entry.panel).opacity : 0;
      entry.target = open;
      entry.generation += 1;
      const generation = entry.generation;
      entry.animation?.cancel();
      entry.animation = null;
      entry.trigger.setAttribute('aria-expanded', String(open));
      entry.panel.inert = !open;
      if (!visible || !motionAllowed() || !entry.panel.animate) {
        settle(entry);
        return;
      }
      // Keep details open through the closing animation; all text stays in the DOM.
      entry.detail.open = true;
      const finish = open ? entry.panel.scrollHeight : 0;
      entry.animation = entry.panel.animate([
        { height: `${start}px`, opacity },
        { height: `${finish}px`, opacity: open ? 1 : 0 },
      ], { duration: 380, easing: ease, fill: 'both' });
      entry.animation.finished.then(() => {
        if (!signal.aborted && entry.generation === generation) settle(entry);
      }).catch(() => {});
    };
    const enter = () => {
      if (entered || !visible || document.hidden) return;
      entered = true;
      if (!motionAllowed() || !media.animate) return;
      entranceAnimations = [...root.querySelectorAll('[data-story-reveal]'), media].map((element, index) => {
        const animation = element.animate([
          { opacity: 0, transform: `translateY(${element === media ? 24 : 14}px)` },
          { opacity: 1, transform: 'translateY(0)' },
        ], { duration: 800, delay: index * 85, easing: ease, fill: 'backwards' });
        animation.finished.catch(() => {});
        return animation;
      });
    };
    const syncScroll = () => {
      scrollFrame = 0;
      if (!desktop.matches || !visible || document.hidden) return;
      const focusLine = Math.min(500, Math.max(200, innerHeight * .48));
      let next = 0;
      entries.forEach((entry, index) => {
        if (entry.detail.getBoundingClientRect().top <= focusLine) next = index;
      });
      if (next !== active) select(next);
    };
    const queueScroll = () => {
      if (!scrollFrame && desktop.matches && visible && !document.hidden) {
        scrollFrame = requestAnimationFrame(syncScroll);
      }
    };
    const syncLayout = () => {
      interaction += 1;
      entries.forEach((entry, index) => {
        entry.target = desktop.matches || index === active;
        settle(entry);
      });
      resetPointer();
      queueScroll();
    };
    const syncMotion = () => {
      root.classList.toggle('is-motion-disabled', !motionAllowed());
      if (!motionAllowed()) {
        stopEntrance();
        resetPointer();
        entries.forEach(settle);
      } else if (visible) {
        enter();
        queueScroll();
      }
    };
    entries.forEach((entry, index) => {
      entry.trigger.addEventListener('click', (event) => {
        event.preventDefault();
        const currentInteraction = ++interaction;
        const next = !entry.target;
        select(index);
        entries.forEach((other, i) => {
          if (i === index) setOpen(other, next);
          else if (!desktop.matches) setOpen(other, false);
        });
        // Closing a long chapter above this one can move its heading offscreen.
        // Wait for both panels to settle, then keep the selected heading in view.
        Promise.all(entries.map((other) => other.animation?.finished.catch(() => {}) || Promise.resolve())).then(() => {
          if (signal.aborted || currentInteraction !== interaction || desktop.matches || !next || !visible || document.hidden) return;
          if (entry.trigger.getBoundingClientRect().top < 112) {
            entry.trigger.scrollIntoView({ block: 'start', behavior: motionAllowed() ? 'smooth' : 'auto' });
          }
        });
      }, { signal });
      entry.trigger.addEventListener('keydown', (event) => {
        const next = { ArrowDown: (index + 1) % entries.length, ArrowUp: (index - 1 + entries.length) % entries.length, Home: 0, End: entries.length - 1 }[event.key];
        if (next !== undefined) {
          event.preventDefault();
          entries[next].trigger.focus();
        }
      }, { signal });
    });
    media.addEventListener('pointermove', (event) => {
      if (!visible || !finePointer.matches || !motionAllowed()) return;
      cancelAnimationFrame(pointerFrame);
      pointerFrame = requestAnimationFrame(() => {
        pointerFrame = 0;
        const bounds = media.getBoundingClientRect();
        media.style.setProperty('--story-mx', `${((event.clientX - bounds.left) / bounds.width - .5) * 12}px`);
        media.style.setProperty('--story-my', `${((event.clientY - bounds.top) / bounds.height - .5) * 10}px`);
      });
    }, { passive: true, signal });
    media.addEventListener('pointerleave', resetPointer, { signal });
    window.addEventListener('scroll', queueScroll, { passive: true, signal });
    window.addEventListener('resize', () => {
      entries.forEach(settle);
      queueScroll();
    }, { passive: true, signal });
    desktop.addEventListener('change', syncLayout, { signal });
    reduced.addEventListener('change', syncMotion, { signal });
    finePointer.addEventListener('change', resetPointer, { signal });
    document.addEventListener('visibilitychange', syncMotion, { signal });
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) {
        enter();
        queueScroll();
      } else {
        stopEntrance();
        resetPointer();
        entries.forEach(settle);
        cancelAnimationFrame(scrollFrame);
        scrollFrame = 0;
      }
    }) : null;
    select(0);
    syncLayout();
    syncMotion();
    if (observer) observer.observe(root);
    else {
      visible = true;
      enter();
      queueScroll();
    }
    return () => {
      controller.abort();
      observer?.disconnect();
      cancelAnimationFrame(scrollFrame);
      resetPointer();
      stopEntrance();
      entries.forEach(settle);
    };
  });
})();
